import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { FaFilter, FaPlus, FaSortAmountDown, FaTimes } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import SummaryApi from "../common";
import VerticalCard from "../components/VerticalCard";
import productCategory from "../helpers/productCategory";
import { request } from "../helpers/request";

const CategoryProduct = () => {
  const [data, setData] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const urlSearch = new URLSearchParams(location.search);
  const urlCategoryListinArray = urlSearch.getAll("category");
  const urlBrandListinArray = urlSearch.getAll("brand");

  const urlCategoryListObject = {};
  urlCategoryListinArray.forEach((el) => {
    urlCategoryListObject[el] = true;
  });

  const urlBrandListObject = {};
  urlBrandListinArray.forEach((el) => {
    urlBrandListObject[el] = true;
  });

  const [selectCategory, setSelectCategory] = useState(urlCategoryListObject);
  const [filterCategoryList, setFilterCategoryList] = useState([]);

  const [selectBrand, setSelectBrand] = useState(urlBrandListObject);
  const [filterBrandList, setFilterBrandList] = useState([]);

  const [allBrands, setAllBrands] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [brandLoading, setBrandLoading] = useState(false);
  const [showAllBrands, setShowAllBrands] = useState(false);

  const [sortBy, setSortBy] = useState("");
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      filterCategoryList.forEach((category) =>
        params.append("category", category),
      );
      filterBrandList.forEach((brand) => params.append("brand", brand));
      const query = params.toString();

      const response = await request({
        url: `${SummaryApi.filterProduct.url}${query ? `?${query}` : ""}`,
        method: SummaryApi.filterProduct.method,
      });

      const dataResponse = await response.json();
      setData(dataResponse?.data || []);
    } catch (error) {
      console.error("Error fetching filtered products:", error);
    } finally {
      setLoading(false);
    }
  }, [filterCategoryList, filterBrandList]);

  const fetchBrands = useCallback(async () => {
    setBrandLoading(true);
    try {
      const params = new URLSearchParams();
      filterCategoryList.forEach((category) =>
        params.append("category", category),
      );
      const query = params.toString();

      const response = await request({
        url: `${SummaryApi.getAllBrands.url}${query ? `?${query}` : ""}`,
        method: SummaryApi.getAllBrands.method,
      });
      const result = await response.json();
      if (result.success) {
        setAllBrands(result.data);
      }
    } catch (error) {
      console.error("Error fetching brands:", error);
    } finally {
      setBrandLoading(false);
    }
  }, [filterCategoryList]);

  const fetchCategories = useCallback(async () => {
    setCategoryLoading(true);
    try {
      const response = await request(SummaryApi.getAllCategories);
      const result = await response.json();
      if (result.success) {
        // Merge legacy categories with backend ones
        const legacyCategories = productCategory;
        const backendCategories = result.data.map((c) => ({
          label: c.name,
          value: c.name,
        }));

        // Create a unique list based on value
        const uniqueCategories = [...legacyCategories];
        backendCategories.forEach((bc) => {
          if (!uniqueCategories.find((lc) => lc.value === bc.value)) {
            uniqueCategories.push(bc);
          }
        });

        setAllCategories(uniqueCategories);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setCategoryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
    fetchBrands();
  }, [fetchCategories, fetchBrands]);

  const handleSelectCategory = (e) => {
    const { value, checked } = e.target;
    setSelectCategory((prev) => ({
      ...prev,
      [value]: checked,
    }));
  };

  const handleSelectBrand = (e) => {
    const { value, checked } = e.target;
    setSelectBrand((prev) => ({
      ...prev,
      [value]: checked,
    }));
  };

  const clearFilters = () => {
    setSelectCategory({});
    setSelectBrand({});
    setSortBy("");
    setShowAllBrands(false);
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const arrayOfCategory = Object.keys(selectCategory)
      .map((categoryKeyName) => {
        if (selectCategory[categoryKeyName]) return categoryKeyName;
        return null;
      })
      .filter((el) => el);

    setFilterCategoryList(arrayOfCategory);

    const arrayOfBrand = Object.keys(selectBrand)
      .map((brandKeyName) => {
        if (selectBrand[brandKeyName]) return brandKeyName;
        return null;
      })
      .filter((el) => el);

    setFilterBrandList(arrayOfBrand);

    const urlCategoryFormat = arrayOfCategory.map((el) => `category=${el}`);
    const urlBrandFormat = arrayOfBrand.map((el) => `brand=${el}`);

    const searchString = [...urlCategoryFormat, ...urlBrandFormat].join("&");
    navigate(`/product-category?${searchString}`);
  }, [selectCategory, selectBrand, navigate]);

  const handleOnChangeSortBy = (e) => {
    const { value } = e.target;
    setSortBy(value);

    if (value === "asc") {
      setData((prev) =>
        [...prev].sort((a, b) => a.sellingPrice - b.sellingPrice),
      );
    }
    if (value === "dsc") {
      setData((prev) =>
        [...prev].sort((a, b) => b.sellingPrice - a.sellingPrice),
      );
    }
  };

  return (
    <div className="bg-surface-muted min-h-screen">
      <div className="container mx-auto px-6 py-12">
        {}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="space-y-2">
            <h1 className="text-4xl font-black text-primary tracking-tight">
              Explore Helmets
            </h1>
            <p className="text-gray-500 font-medium">
              Showing {data.length} premium products
            </p>
          </div>

          <div className="flex items-center gap-4">
            {}
            <button
              onClick={() => setShowMobileFilters(true)}
              className="lg:hidden flex items-center justify-center gap-2 h-12 px-6 bg-white border border-surface-border rounded-full font-bold text-sm shadow-sm hover:shadow-md transition-all"
            >
              <FaFilter className="text-xs" /> Filters
            </button>

            {}
            <div className="relative group">
              <select
                value={sortBy}
                onChange={handleOnChangeSortBy}
                className="appearance-none h-12 pl-6 pr-12 bg-white border border-surface-border rounded-full font-bold text-sm shadow-sm outline-none focus:border-accent/20 cursor-pointer min-w-[200px]"
              >
                <option value="" disabled>
                  Sort By
                </option>
                <option value="asc">Price: Low to High</option>
                <option value="dsc">Price: High to Low</option>
              </select>
              <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none">
                <FaSortAmountDown className="text-gray-400 text-xs" />
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-12 relative">
          {}
          <div className="hidden lg:block w-72 h-fit bg-white rounded-card shadow-premium border border-surface-border p-8 sticky top-28 max-h-[calc(100vh-120px)] overflow-y-auto custom-scrollbar">
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-lg font-black text-primary tracking-tight flex items-center gap-2">
                <FaFilter className="text-xs text-accent" /> Filters
              </h3>
              <button
                onClick={clearFilters}
                className="text-xs font-bold text-accent hover:underline"
              >
                Clear All
              </button>
            </div>

            <div className="space-y-8">
              <div>
                <h4 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-4">
                  Category
                </h4>
                <div className="flex flex-col gap-3">
                  {categoryLoading ? (
                    <div className="text-xs text-gray-400 italic">
                      Updating categories...
                    </div>
                  ) : (
                    allCategories.map((category) => (
                      <label
                        key={category.value}
                        className="group flex items-center gap-3 cursor-pointer"
                      >
                        <div className="relative flex items-center justify-center">
                          <input
                            type="checkbox"
                            checked={selectCategory[category.value] || false}
                            value={category.value}
                            onChange={handleSelectCategory}
                            className="peer appearance-none w-5 h-5 border-2 border-surface-border rounded-md checked:bg-accent checked:border-accent transition-all"
                          />
                          <FaPlus className="absolute text-white text-[10px] opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none" />
                        </div>
                        <span className="text-sm font-bold text-gray-600 group-hover:text-primary transition-colors">
                          {category.label}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* Brand Filter */}
              <div>
                <h4 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-4">
                  Brand
                </h4>
                <div className="flex flex-col gap-3">
                  {brandLoading ? (
                    <div className="text-xs text-gray-400">
                      Loading brands...
                    </div>
                  ) : allBrands.length === 0 ? (
                    <div className="text-xs text-gray-400">No brands found</div>
                  ) : (
                    <>
                      {(showAllBrands ? allBrands : allBrands.slice(0, 6)).map(
                        (brand) => (
                          <label
                            key={brand}
                            className="group flex items-center gap-3 cursor-pointer"
                          >
                            <div className="relative flex items-center justify-center">
                              <input
                                type="checkbox"
                                checked={selectBrand[brand] || false}
                                value={brand}
                                onChange={handleSelectBrand}
                                className="peer appearance-none w-5 h-5 border-2 border-surface-border rounded-md checked:bg-accent checked:border-accent transition-all"
                              />
                              <FaPlus className="absolute text-white text-[10px] opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none" />
                            </div>
                            <span className="text-sm font-bold text-gray-600 group-hover:text-primary transition-colors">
                              {brand}
                            </span>
                          </label>
                        ),
                      )}
                      {allBrands.length > 6 && (
                        <button
                          onClick={() => setShowAllBrands(!showAllBrands)}
                          className="text-xs font-bold text-accent hover:underline text-left mt-2"
                        >
                          {showAllBrands ? "Show Less" : "Show More"}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="flex-1">
            <AnimatePresence mode="wait">
              {loading ? (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <VerticalCard loading={true} />
                </motion.div>
              ) : data.length > 0 ? (
                <motion.div
                  key="content"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <VerticalCard data={data} loading={false} />
                </motion.div>
              ) : (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="h-[60vh] flex flex-col items-center justify-center bg-white rounded-card border border-surface-border text-center p-12"
                >
                  <div className="w-20 h-20 bg-surface-muted rounded-full flex items-center justify-center mb-6">
                    <FaTimes className="text-gray-300 text-3xl" />
                  </div>
                  <h3 className="text-2xl font-black text-primary mb-2">
                    No Products Found
                  </h3>
                  <p className="text-gray-500 mb-8 max-w-xs">
                    Try adjusting your filters or category selection to find
                    what you're looking for.
                  </p>
                  <button
                    onClick={clearFilters}
                    className="px-8 py-3 bg-primary text-white font-bold rounded-full hover:bg-accent transition-all"
                  >
                    Clear Filters
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      <AnimatePresence>
        {showMobileFilters && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowMobileFilters(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-md z-[110]"
            />
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed inset-y-0 right-0 h-full w-[85%] max-w-[400px] bg-white z-[120] shadow-2xl flex flex-col"
            >
              <div className="p-6 border-b border-surface-border flex items-center justify-between sticky top-0 bg-white z-10">
                <div className="flex items-center gap-2">
                  <FaFilter className="text-accent" />
                  <h3 className="text-xl font-black text-primary">Filters</h3>
                </div>
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="w-10 h-10 flex items-center justify-center rounded-xl bg-surface-muted text-gray-500"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-10 custom-scrollbar">
                {/* Category Section */}
                <div className="space-y-6">
                  <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-400 border-b border-surface-border pb-2">
                    Shop By Category
                  </h4>
                  <div className="grid grid-cols-1 gap-1">
                    {allCategories.map((category) => (
                      <label
                        key={category.value}
                        className="flex items-center justify-between p-4 rounded-2xl hover:bg-surface-muted cursor-pointer transition-colors active:scale-[0.98]"
                      >
                        <span className="text-base font-bold text-gray-700 leading-none">
                          {category.label}
                        </span>
                        <div className="relative flex items-center justify-center">
                          <input
                            type="checkbox"
                            checked={selectCategory[category.value] || false}
                            value={category.value}
                            onChange={handleSelectCategory}
                            className="peer appearance-none w-6 h-6 border-2 border-surface-border rounded-lg checked:bg-accent checked:border-accent transition-all"
                          />
                          <FaPlus className="absolute text-white text-[10px] opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none" />
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Brand Section */}
                <div className="space-y-6">
                  <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-400 border-b border-surface-border pb-2">
                    Shop By Brand
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {brandLoading ? (
                      <div className="text-sm text-gray-400 py-4">
                        Updating list...
                      </div>
                    ) : (
                      <>
                        {allBrands.map((brand) => (
                          <label
                            key={brand}
                            className={`
                                                            px-4 py-3 rounded-xl border-2 font-bold text-sm transition-all cursor-pointer select-none active:scale-95
                                                            ${
                                                              selectBrand[brand]
                                                                ? "border-accent bg-accent/5 text-accent shadow-md shadow-accent/5"
                                                                : "border-surface-border bg-white text-gray-500 hover:border-gray-300"
                                                            }
                                                        `}
                          >
                            <input
                              type="checkbox"
                              checked={selectBrand[brand] || false}
                              value={brand}
                              onChange={handleSelectBrand}
                              className="hidden"
                            />
                            {brand}
                          </label>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-surface-border bg-white mt-auto sticky bottom-0 flex gap-3">
                <button
                  onClick={() => {
                    clearFilters();
                    setShowMobileFilters(false);
                  }}
                  className="flex-1 py-4 bg-surface-muted text-primary font-black rounded-2xl active:scale-95 transition-all text-sm"
                >
                  Reset All
                </button>
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="flex-[1.5] py-4 bg-primary text-white font-black rounded-2xl shadow-xl shadow-primary/10 active:scale-95 transition-all text-sm"
                >
                  Apply Filters
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CategoryProduct;
